import { useEffect, useRef, useState } from "react";
import {
  IonModal,
  IonContent,
  IonHeader,
  IonToolbar,
  IonRadio,
  IonRadioGroup,
} from "@ionic/react";
import { useSelector } from "react-redux";
import { useMixpanel } from "../../../Context/MixpanelContext/MixpanelProvider";
import { actions } from "../../../Redux/store";
import { hapticsImpactLight } from "../../../lib/utils";
import addNewLeagu from "../../../assets/imgs/add-new-league.svg";
import LeagueSelectionSkeleton from "../LeagueSelectionModal/LeagueSelectionSkeleton";
import { useLeagueInfo } from "../../../Context/LeagueInfoContext/LeagueInfoContext";
import { useLeagueStore } from "../../../zustandStore/leagueStore";
import { useSocket } from "../../../Context/SocketContext/SocketProvider";
import { TextShimmer } from "../../ui/text-shimmer";
import { BetaBadge } from "../../BetaBadge/BetaBadge";

// Helper function to format league status
const formatLeagueStatus = (status: string) => {
  switch (status) {
    case "NOT_STARTED":
      return "Not started";
    case "PENDING":
      return "Syncing";
    case "IN_PROGRESS":
      return "Syncing";
    case "COMPLETED":
      return "Completed";
    case "FAILED_TO_PROCESS":
      return "Failed to process";
    case "FAILED_TO_SYNC":
      return "Failed to sync";
    default:
      return status;
  }
};

export default function AddNewLeague() {
  const { selectedLeague: storedLeague, setSelectedLeague: setStoredLeague } =
    useLeagueStore();
  const [selectedLeagueId, setSelectedLeagueId] = useState<any>(
    storedLeague?.leagueId,
  );
  const Mixpanel = useMixpanel();
  const { startNewThread } = useSocket();
  const { refetchLeagueInfo, filteredLeagueData, isLoading } = useLeagueInfo();
  const isAddNewLeagueModal = useSelector(
    (sate: any) => sate.modal.AddNewLeague?.open,
  );

  const modal = useRef<HTMLIonModalElement>(null);

  const closeAddNewLeagueModal = () => {
    actions.modal.closeAddNewLeagueModal(null);
  };

  function dismiss() {
    modal.current?.dismiss();
    setSelectedLeagueId(null);
    closeAddNewLeagueModal();
  }

  const handleRadioChange = (event: any) => {
    const clickedLeagueId = event.target.value;
    hapticsImpactLight();
    // Toggle selection: if already selected, unselect it
    if (selectedLeagueId === clickedLeagueId) {
      setSelectedLeagueId(null);
      // Clear stored league data when unselecting
      setStoredLeague(null);
      startNewThread();
    } else {
      setSelectedLeagueId(clickedLeagueId);
      // Find the complete league object and trigger continue functionality
      const selectedLeagueObject = filteredLeagueData.find(
        (league: any) => league.leagueId === clickedLeagueId,
      );
      setStoredLeague(selectedLeagueObject);
      startNewThread(
        selectedLeagueObject?.leagueId,
        selectedLeagueObject?.ownerId,
      );
      console.log("League saved to store:", selectedLeagueObject);
      dismiss();
      actions.modal.openChatbotSearchModal(null);

      Mixpanel.trackEvent("select_league", {
        name: "User selects league",
        league_name:clickedLeagueId.league_name
      });
    }
  };

  const handleCardClick = (leagueCard: any) => {
    // Only allow selection if league status is COMPLETED
    if (leagueCard?.status === "COMPLETED") {
      hapticsImpactLight();

      // Toggle selection: if already selected, unselect it
      if (selectedLeagueId === leagueCard?.leagueId) {
        setSelectedLeagueId(null);
        // Clear stored league data when unselecting
        setStoredLeague(null);
        startNewThread();
      } else {
        setSelectedLeagueId(leagueCard?.leagueId);
        // Trigger continue functionality immediately
        setStoredLeague(leagueCard);
        startNewThread(leagueCard?.leagueId, leagueCard?.ownerId);
        dismiss();
        actions.modal.openChatbotSearchModal(null);
        Mixpanel.trackEvent("select_league", {
          name: "User selects league",
          league_name:leagueCard.league_name
        });
      }
    }
  };

  useEffect(() => {
    if (isAddNewLeagueModal) {
      refetchLeagueInfo();
    }
  }, [isAddNewLeagueModal]);

  // Update selectedLeagueId when storedLeague changes or modal opens
  useEffect(() => {
    if (isAddNewLeagueModal && storedLeague?.leagueId) {
      setSelectedLeagueId(storedLeague.leagueId);
    }
  }, [isAddNewLeagueModal, storedLeague]);

  const toggleLeagueModal = () => {
    hapticsImpactLight();
    actions.modal.openLeagueModal(null);
    Mixpanel.trackEvent("add_a_league_selected", {
      name: "User opens select new league modal",
      league: {
        leagueId: storedLeague?.leagueId,
        leagueName: storedLeague?.league_name,
      },
    });
  };

  return (
    <IonModal
      expandToScroll={false}
      onDidDismiss={dismiss}
      className="dynamic-height-modal"
      isOpen={isAddNewLeagueModal}
      backdropDismiss={true}
      initialBreakpoint={1}
      breakpoints={[0, 1]}
      ref={modal}
    >
      <IonHeader className="ion-no-border modal-ion-header">
        <IonToolbar className="custom-toolbar">
          <div className="flex items-center justify-center w-full px-4 pt-8">
            <span className="text-white text-[20px] font-medium leading-[140%] text-center">
              Select your league
            </span>
            <div className="ml-2">
              <BetaBadge />
            </div>
          </div>
        </IonToolbar>
      </IonHeader>

      <div className="league-content-section relative">
        <div
          className="px-[24px]"
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom) + 70px)",
          }}
        >
          {isLoading ? (
            <LeagueSelectionSkeleton />
          ) : filteredLeagueData.length === 0 ? (
            <div className="p-4 mt-14 text-center text-white/80 pb-5">
              <p className="font-medium">No league found</p>
            </div>
          ) : (
            <IonRadioGroup
              value={selectedLeagueId}
              onIonChange={handleRadioChange}
            >
              <div className="flex-col justify-center pt-6 space-y-[20px] cursor-pointer">
                {filteredLeagueData.map((leagueCard: any, index: any) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between w-full p-3 ${leagueCard?.status !== "COMPLETED"
                      ? "cursor-not-allowed"
                      : "cursor-pointer"
                      }`}
                    onClick={() => handleCardClick(leagueCard)}
                  >
                    <div className="flex-col space-y-[2px]">
                      <p
                        className={`text-white text-[16px] font-medium ${leagueCard?.status !== "COMPLETED" ? "opacity-50" : ""
                          }`}
                      >
                        {leagueCard?.league_name}
                      </p>
                      {leagueCard?.status === "PENDING" ||
                        leagueCard?.status === "IN_PROGRESS" ? (
                        <TextShimmer
                          duration={3}
                          className="text-white/70 text-[12px] font-normal [--base-color:theme(colors.gray.500)] [--base-gradient-color:theme(colors.white)] dark:[--base-color:theme(colors.gray.500)] dark:[--base-gradient-color:theme(colors.gray.300)]"
                        >
                          {formatLeagueStatus(leagueCard?.status)}
                        </TextShimmer>
                      ) : (
                        <p className="text-white/70 text-[12px] font-normal">
                          {formatLeagueStatus(leagueCard?.status)}
                        </p>
                      )}
                    </div>
                    <div
                      className={`h-full flex items-center justify-center ${leagueCard?.status !== "COMPLETED" ? "opacity-50" : ""
                        }`}
                    >
                      <IonRadio
                        class="custom-radio"
                        mode="md"
                        value={leagueCard?.leagueId}
                        disabled={leagueCard?.status !== "COMPLETED"}
                        aria-label="Custom checkbox"
                      ></IonRadio>
                    </div>
                  </div>
                ))}
              </div>
            </IonRadioGroup>
          )}
        </div>
        {!isLoading && (
          <div
            className="fixed bottom-0 left-0 right-0 pt-4 bg-[#000513] flex flex-col items-center z-10"
            style={{
              paddingBottom: "calc(env(safe-area-inset-bottom) + 10px)",
            }}
          >
            <div
              className="text-center flex items-center gap-2 pb-4"
              onClick={toggleLeagueModal}
            >
              <img src={addNewLeagu} alt="addNewLeagu" />
              <span className="text-white text-[12px] font-medium">
                Add New League
              </span>
            </div>
          </div>
        )}
      </div>
    </IonModal>
  );
}
